import React, { forwardRef } from "react";
import { bool, node, oneOf } from "prop-types";

import {
  BRAND,
  BRAND_DARK,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  WHITE
} from "../colors";
import actionElements from "../constants/actionElements";
import { createTextAction } from "./styled";
import textActionTypography from "./textActionTypography";

const TextAction = forwardRef(
  (
    {
      children,
      color,
      element,
      href,
      onClick,
      type,
      typography,
      underlined,
      ...passThroughProps
    },
    ref
  ) => {
    const TextActionBase = createTextAction(typography);

    return (
      <TextActionBase
        ref={ref}
        as={element}
        color={color}
        href={element === actionElements.a ? href : null}
        onClick={onClick}
        type={element === actionElements.button ? type : null}
        underlined={underlined}
        {...passThroughProps}
      >
        {children}
      </TextActionBase>
    );
  }
);

TextAction.propTypes = {
  /**
   * The content of the link or button rendered by TextAction. Typically
   * a string but occasionally a node containing an icon image and a string.
   * */
  children: node.isRequired,
  color: oneOf([BRAND, BRAND_DARK, TEXT_PRIMARY, TEXT_SECONDARY, WHITE]),
  /*
   * TextAction can be configured to render either an HTML ancor element or
   * and HTML button element. By default, TextAction will render an anchor element.
   * Valid element types are referenced through the actionElements enum
   * */
  element: oneOf(Object.values(actionElements)),
  /*
   * When TextAction renders an anchor element, href is required.
   * */
  href: props => {
    if (!props.href && props.element === actionElements.a) {
      return new Error("The href prop is required when TextAction is a link");
    }

    if (typeof props.href !== "string" && props.element === actionElements.a) {
      return new Error("href must be a string");
    }

    return null;
  },
  /*
   * When TextAction renders a button element, an onClick handler is required.
   * onClick handlers may also be applied to TextAction components that render
   * anchor tags..
   * */
  onClick: props => {
    if (!props.onClick && props.element === actionElements.button) {
      return new Error(
        "The onClick prop is required when TextAction is a button"
      );
    }

    if (props.onClick && typeof props.onClick !== "function") {
      return new Error("onClick must be either null or a function");
    }

    return null;
  },
  type: oneOf(["button", "submit", "reset"]),
  /**
   * Determines the typography styles inherited by TextAction.
   * Values for the typography prop are imported from the textActionTypography module.
   * */
  typography: oneOf(Object.values(textActionTypography)),
  underlined: bool
};

TextAction.defaultProps = {
  color: BRAND,
  element: actionElements.a,
  href: null,
  onClick: null,
  type: "submit",
  typography: textActionTypography.BODY,
  underlined: true
};

export default TextAction;
